import React, { useState, useMemo } from 'react';
import JSZip from 'jszip';
import { getXlsxSheetNames } from '../../../lib/xlsxUtils';
import { getBytesfromMB } from '../../../lib/helpers';
import { REQUIRED_CSV_FILES, REQUIRED_EXCEL_SHEETS, RV_TOOLS_UPLOAD_SIZE_LIMIT } from '../../../constants/discovery';
import { SelectZipFile } from './components/SelectZipFile';
import { UploadSteps } from './components/UploadSteps';
import { CSVDataViewer } from './components/CSVDataViewer';
import { ReviewContents } from './components/ReviewContents';
import { SendingToInventory } from './components/SendingToInventory';
import { ImportHistory } from './components/ImportHistory';
import { useUploadRVToolsExportDataMutation, useGetImportHistoryQuery } from '../../../api/discovery/inventory';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { getSessionVar } from '../../../lib/helpers';
import { InvalidFileError, MissingOrCorruptFilesError, UncompressedSizeError } from './components/ValidationErrors';

import { Upload, Typography, LoaderAnimation, Form } from '../../../components';

const { Paragraph, Title } = Typography;

import './RVTools.scss';

export const RVTools = () => {
    const [fileList, setFileList] = useState([]);
    const [step, setStep] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [activeImportId, setActiveImportId] = useState(null);
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);
    const MAX_COMPRESSED_FILE_SIZE = useMemo(
        () =>
            getSessionVar('discovery_multipart_compressed_max_file_size_in_megabytes') ||
            RV_TOOLS_UPLOAD_SIZE_LIMIT.compressedMB,
        [],
    ); // Default to 10MB if not set
    const MAX_UNCOMPRESSED_FILE_SIZE = useMemo(
        () =>
            getSessionVar('discovery_multipart_uncompressed_max_file_size_in_megabytes') ||
            RV_TOOLS_UPLOAD_SIZE_LIMIT.uncompressedMB,
        [],
    ); // Default to 50MB if not set

    const [uploadRVToolsExportData] = useUploadRVToolsExportDataMutation();
    const { data: historyData, isLoading: isLoadingHistory } = useGetImportHistoryQuery(
        { orgId, toolId: 'rv_tools' },
        { skip: !orgId, refetchOnMountOrArgChange: true, keepUnusedDataFor: 0 },
    );

    const { notifyError } = useNotificationContext();

    const [form] = Form.useForm();

    // Check for valid ZIP file and required CSV's
    const validateZip = async (file) => {
        try {
            // Will check for a valid .zip archive as well as a renamed file extension
            const zip = await JSZip.loadAsync(file);
            const fileNames = Object.keys(zip.files).map((path) => path.split('/').pop());

            // Calculate total uncompressed size
            let totalUncompressedSize = 0;
            Object.values(zip.files).forEach((zipEntry) => {
                if (!zipEntry.dir) {
                    totalUncompressedSize += zipEntry._data.uncompressedSize;
                }
            });

            // Check if uncompressed size exceeds MAX_UNCOMPRESSED_FILE_SIZE
            const maxUncompressedSizeInBytes = getBytesfromMB(MAX_UNCOMPRESSED_FILE_SIZE);
            if (totalUncompressedSize > maxUncompressedSizeInBytes) {
                setValidationError({
                    message: <UncompressedSizeError maxSize={MAX_UNCOMPRESSED_FILE_SIZE} />,
                });
                return false;
            }

            // Check if all required files exist
            const missingFiles = REQUIRED_CSV_FILES.filter((required) => !fileNames.includes(required));

            if (missingFiles.length > 0) {
                setValidationError({
                    message: <MissingOrCorruptFilesError missingFiles={missingFiles} fileType="csv" />,
                });
                return false;
            }
            return true;
        } catch {
            setValidationError({
                message: <InvalidFileError />,
            });
            return false;
        }
    };

    const validateExcel = async (file) => {
        try {
            const sheetNames = await getXlsxSheetNames(file);

            const missingSheets = REQUIRED_EXCEL_SHEETS.filter(
                (required) => !sheetNames.some((name) => name.includes(required)),
            );

            if (missingSheets.length > 0) {
                setValidationError({
                    message: <MissingOrCorruptFilesError missingFiles={missingSheets} fileType="excel" />,
                });
                return false;
            }
            return true;
        } catch {
            setValidationError({
                message: <InvalidFileError />,
            });
            return false;
        }
    };

    const resetForm = () => {
        setValidationError(null);
        setIsUploading(false);
        setFileList([]);
        setStep(0);
    };

    const uploadProps = {
        name: 'rvtoolsCSVUpload',
        multiple: false,
        accept: '.zip, .xlsx',
        beforeUpload: async (file) => {
            setIsVerifying(true);
            const maxSizeInBytes = getBytesfromMB(MAX_COMPRESSED_FILE_SIZE);
            if (file.size > maxSizeInBytes) {
                setValidationError({
                    message: (
                        <>
                            <Title level={2}>File size error</Title>
                            <Paragraph>
                                File size exceeds the maximum allowed size of {MAX_COMPRESSED_FILE_SIZE} MB.
                            </Paragraph>
                        </>
                    ),
                });
                setIsVerifying(false);
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
            const isValid = isExcel ? await validateExcel(file) : await validateZip(file);
            setIsVerifying(false);
            if (!isValid) {
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            return false; // Prevents automatic upload after successfully validating zip file
        },
        fileList: fileList,
        onChange(info) {
            setFileList(info.fileList);
        },
    };

    const handleDelete = () => {
        setFileList([]);
        setStep(0);
    };

    const addToInventory = async () => {
        try {
            const importName = form.getFieldValue('importName');
            const result = await uploadRVToolsExportData({
                file: fileList[0]?.originFileObj,
                orgId,
                importName,
            }).unwrap();
            setActiveImportId(result?.id ?? null);
            setStep(3);
        } catch (error) {
            if (error?.status === 415) {
                notifyError({
                    message: 'Unsupported File Type',
                    description: 'The file format is not supported. Please upload a valid ZIP, XLS, or XLSX file.',
                    duration: 10,
                });
            } else {
                notifyError({
                    message: 'Upload Failed',
                    description: error?.data?.message || 'An error occurred while uploading RVTools data.',
                    duration: 10,
                });
            }
            resetForm();
        }
    };

    const stepContent = {
        0: (
            <SelectZipFile
                isVerifying={isVerifying}
                validationError={validationError}
                resetForm={resetForm}
                fileList={fileList}
                uploadProps={uploadProps}
                handleDelete={handleDelete}
                setStep={setStep}
                form={form}
            />
        ),
        1: <LoaderAnimation size="small" color="grayscale" />,
        2: <ReviewContents resetForm={resetForm} addToInventory={addToInventory} isUploading={isUploading} />,
        3: <SendingToInventory setStep={setStep} activeImportId={activeImportId} />,
    };

    return (
        <div className="discovery-rvtools">
            <div className="rvtools-page-header">
                <Title level={2} className="rvtools-page-title">
                    Import New RVTools Dump
                </Title>
                <Paragraph>
                    Upload your RV Tools CSV or Excel export to populate your Inventory. We&apos;ll analyze your VMs and
                    hosts to surface insights on capacity, performance, and rightsizing potential.
                </Paragraph>
            </div>
            <div>
                <div className="csv-upload-dialog">
                    <div className="rv-tools-left-col-upload">
                        <UploadSteps step={step} validationError={validationError} />
                    </div>
                    <div className="rv-tools-right-col-upload">{stepContent[step] || null}</div>
                </div>
                {[1, 2].includes(step) && !isUploading && (
                    <CSVDataViewer zipOrCSVFile={fileList[0]?.originFileObj} setStep={setStep} />
                )}
                {!isUploading && historyData?.content.length > 0 && (
                    <ImportHistory historyData={historyData?.content} isLoading={isLoadingHistory} />
                )}
                {isUploading && <LoaderAnimation color="blueBackground" size="small" />}
            </div>
        </div>
    );
};
